import React, { useState, useEffect, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import DataTable from '../../Common/DataTable';
import { fetchTableData } from '../../../Api/ApiMethods';
import { useNavigate } from 'react-router-dom';
import { CustomerNameFilter, CustomerNumberFilter, PlateFilter } from '../../Common/Filters/CommonFilters';
import { customerDetailButtonColumn } from '../../Common/TableColumns';
import useDebouncedValue from '../../../_helper/useDebouncedValue';
import { appendQueryParamIfMinLength, normalizeSearchFields } from '../../../_helper/regexHandler';

const LicensePlates = () => {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [customerNumberSearch, setCustomerNumberSearch] = useState('');
    const [customerNameSearch, setCustomerNameSearch] = useState('');
    const [plateSearch, setPlateSearch] = useState('');

    const debouncedCustomerNumberSearch = useDebouncedValue(customerNumberSearch, 500);
    const debouncedCustomerNameSearch = useDebouncedValue(customerNameSearch, 500);
    const debouncedPlateSearch = useDebouncedValue(plateSearch, 500);

    const licensePlateApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/licenseplate';
    const navigate = useNavigate();

    useEffect(() => {
        let isMounted = true;
        const fetchLicensePlates = async () => {
            const { normalized, hasInvalid } = normalizeSearchFields([
                { key: 'customerNumber', value: debouncedCustomerNumberSearch, minLength: 3 },
                { key: 'customerName', value: debouncedCustomerNameSearch, minLength: 2 },
                { key: 'plate', value: debouncedPlateSearch, minLength: 2 }
            ]);

            if (hasInvalid) {
                if (isMounted) setIsLoading(false);
                return;
            }

            setIsLoading(true);
            const queryParams = new URLSearchParams();

            appendQueryParamIfMinLength(queryParams, 'taxOrIdentityNumber', normalized.customerNumber, 3);
            appendQueryParamIfMinLength(queryParams, 'customerNameOrCompanyName', normalized.customerName, 2);
            appendQueryParamIfMinLength(queryParams, 'licensePlate', normalized.plate, 2);

            const apiUrl = queryParams.toString()
                ? `${licensePlateApiUrl}?${queryParams.toString()}`
                : licensePlateApiUrl;

            await fetchTableData({
                apiEndpoint: apiUrl,
                page,
                pageSize,
                search: null,
                setDataCallback: setData,
                setTotalRowsCallback: setTotalRows,
            });

            if (isMounted) setIsLoading(false);
        };

        fetchLicensePlates();

        return () => {
            isMounted = false;
        };
    }, [page, pageSize, debouncedCustomerNumberSearch, debouncedCustomerNameSearch, debouncedPlateSearch, licensePlateApiUrl]);

    const handleCustomerDetail = (customerId) => {
        navigate(`/customer/${customerId}`);
    };

    const columns = [
        customerDetailButtonColumn({
            getCustomerId: (row) => row.customer?.[0],
            onClick: handleCustomerDetail
        }),
        { name: 'Plaka Numarası', selector: row => row.licensePlateNumber || '', sortable: true },
        { name: 'Plaka Durumu', selector: row => row.licensePlateStatus || '', sortable: true },
        { name: 'Şasi Numarası', selector: row => row.carchassisNumber || '', sortable: true },
        { name: 'Araç Markası', selector: row => row.carBrand || '', sortable: true },
        { name: 'Araç Modeli', selector: row => row.carModel || '', sortable: true },
        { name: 'Araç Yılı', selector: row => row.carYear || '', sortable: true },
        { name: 'Araç Sahibi', selector: row => row.carOwner || '', sortable: true },
        { name: 'Trafik Sigortası Numarası', selector: row => row.trafficInsurancePolicy?.trafficInsuranceNumber || '', sortable: true },
        { name: 'Kasko Sigortası Numarası', selector: row => row.cascoInsurancePolicy?.cascoInsuranceNumber || '', sortable: true },
        { name: 'Açıklama', selector: row => row.description || '', sortable: true },
    ];

    return (
        <>
            <Breadcrumbs parent='Müşteri Alanları' title='Plakalar' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Plakalar</H5>
                                <span>Mevcut olan plakalar aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <Row className="g-3 mb-3 align-items-center">
                                    <CustomerNumberFilter
                                        md={4}
                                        value={customerNumberSearch}
                                        onChange={(e) => { setCustomerNumberSearch(e.target.value); setPage(1); }}
                                    />
                                    <CustomerNameFilter
                                        md={4}
                                        value={customerNameSearch}
                                        onChange={(e) => { setCustomerNameSearch(e.target.value); setPage(1); }}
                                    />
                                    <PlateFilter
                                        md={4}
                                        value={plateSearch}
                                        onChange={(e) => { setPlateSearch(e.target.value); setPage(1); }}
                                    />
                                </Row>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        pagination
                                        paginationServer
                                        stickyActionPosition="left"
                                        selectableRows={false}
                                        paginationTotalRows={totalRows}
                                        paginationPerPage={pageSize}
                                        paginationDefaultPage={page}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={pageSize => setPageSize(pageSize)}
                                        progressPending={isLoading}
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={isLoading ? null : "Plaka bulunamadı"}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default LicensePlates;
